import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

// Modal Ctrl+K care caută în întreaga platformă, scope-uit per rol.
// Trigger: shortcut Ctrl+K (sau Cmd+K), buton iconiță în topbar (component ButtonGlobalSearch),
// sau direct prin prop `open`. Listener-ul global e instalat în Layout.
//
// Navigare cu tastatura: ↑/↓ schimbă selecția, Enter deschide rezultatul, Esc închide.
// Mobile: full-screen modal sub 768px (clasele Tailwind aplicate condițional).

// Categorii UI — ordinea în care apar; iconițele rămân stroke heroicons consistente.
// `pages` apare prima ca să dea acces rapid la navigare (search Cmd-K stilul Notion).
const CATEGORY_META = {
  pages: { iconKey: 'arrow' },
  companies: { iconKey: 'building' },
  employees: { iconKey: 'users' },
  courses: { iconKey: 'book' },
  certificates: { iconKey: 'document' },
  departments: { iconKey: 'rectGroup' },
  documents: { iconKey: 'document' },
  notifications: { iconKey: 'bell' },
  auditLogs: { iconKey: 'shield' },
};

const ICONS = {
  building: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21"
    />
  ),
  users: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
    />
  ),
  book: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
    />
  ),
  document: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
    />
  ),
  rectGroup: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 0 1-1.125-1.125v-3.75ZM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-8.25ZM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-2.25Z"
    />
  ),
  bell: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
    />
  ),
  shield: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
    />
  ),
  arrow: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
  ),
};

const Icon = ({ name }) => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    {ICONS[name]}
  </svg>
);

const GlobalSearch = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-focus input la deschidere; reset la închidere.
  useEffect(() => {
    if (open) {
      setQuery('');
      setData(null);
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.trim().length < 2) {
      setData(null);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      api
        .get(`/search/global?q=${encodeURIComponent(query.trim())}`)
        .then((res) => {
          setData(res.data);
          setActiveIndex(0);
        })
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  // Lista plată a tuturor rezultatelor — utilă pentru navigare cu tastatura.
  const flatResults = useMemo(() => {
    if (!data?.results) return [];
    const out = [];
    Object.entries(CATEGORY_META).forEach(([cat]) => {
      (data.results[cat] || []).forEach((item) => out.push({ ...item, category: cat }));
    });
    return out;
  }, [data]);

  const handleSelect = useCallback(
    (item) => {
      if (!item?.href) return;
      if (onClose) onClose();
      navigate(item.href);
    },
    [navigate, onClose]
  );

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (onClose) onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(flatResults.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatResults[activeIndex];
        if (item) handleSelect(item);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flatResults, activeIndex, handleSelect, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-slate-900/60 backdrop-blur-sm p-4 sm:pt-24"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[85vh] sm:max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <svg
            className="w-5 h-5 text-slate-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
            />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('globalSearch.placeholder')}
            className="flex-1 bg-transparent border-0 outline-none text-base text-slate-900 dark:text-slate-100 placeholder-slate-400"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('globalSearch.searching')}
            </div>
          )}

          {!loading && !data && query.trim().length < 2 && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('globalSearch.minChars')}
              </p>
              <div className="mt-3 flex flex-wrap justify-center gap-2 text-[11px] text-slate-400 dark:text-slate-500">
                <span>
                  {t('globalSearch.shortcut')}:{' '}
                  <kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">Ctrl+K</kbd>
                </span>
                <span>
                  {t('globalSearch.navigate')}:{' '}
                  <kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">↑↓</kbd>
                </span>
                <span>
                  {t('globalSearch.select')}:{' '}
                  <kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">Enter</kbd>
                </span>
              </div>
            </div>
          )}

          {!loading && data && data.totalCount === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('globalSearch.noResults', { query: query.trim() })}
            </div>
          )}

          {!loading && data && data.totalCount > 0 && (
            <div className="py-2">
              {Object.entries(CATEGORY_META).map(([cat, meta]) => {
                const items = data.results[cat] || [];
                if (items.length === 0) return null;
                return (
                  <div key={cat} className="mb-2">
                    <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t(`globalSearch.categories.${cat}`)} ({items.length})
                    </p>
                    {items.map((item) => {
                      // Index global pentru highlight cu tastatura
                      const globalIdx = flatResults.findIndex(
                        (r) => r.category === cat && r.id === item.id
                      );
                      const isActive = globalIdx === activeIndex;
                      return (
                        <button
                          key={`${cat}-${item.id}`}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setActiveIndex(globalIdx)}
                          className={`w-full flex items-start gap-3 px-4 py-2 text-left transition-colors ${
                            isActive
                              ? 'bg-primary-50 dark:bg-primary-950/30'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                          }`}
                        >
                          <span
                            className={`flex-shrink-0 mt-0.5 ${isActive ? 'text-primary-600 dark:text-primary-300' : 'text-slate-400 dark:text-slate-500'}`}
                          >
                            <Icon name={meta.iconKey} />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                              {/* Pentru tip „pagină", label-ul vine din i18n folosind cheia (item.id) */}
                              {cat === 'pages'
                                ? t(`globalSearch.pages.${item.id}`, { defaultValue: item.id })
                                : item.label}
                            </span>
                            {item.sublabel && (
                              <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                                {item.sublabel}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer cu hint navigare */}
        {data && data.totalCount > 0 && (
          <div className="hidden sm:flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-[10px] text-slate-500 dark:text-slate-400">
            <span>{t('globalSearch.totalResults', { count: data.totalCount })}</span>
            <span className="flex items-center gap-2">
              <span>
                <kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">↑↓</kbd>{' '}
                {t('globalSearch.navigate')}
              </span>
              <span>
                <kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">↵</kbd>{' '}
                {t('globalSearch.select')}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;
